import {
  Box,
  Button,
  Card as CardMUI,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import styled from "styled-components";

import { FlexGrid } from "../../common/grid/components/FlexGrid";

type CardHeaderProps = {
  $backgroundColor?: string;
};

const colors = {
  primary: "#0b234c",
  secondary: "#146cfa",
  orange: "#ff5000",
  orangeHover: "#e54800",
  grayText: "#5d5958",
  lightGrayText: "#979797",
  divider: "#d2d2d2",
  registry: "#5a5a5a",
};

export const StyledBox = styled(Box)`
  && {
    max-width: 1600px;
    margin: auto;
    padding: 60px 120px;
  }

  @media (max-width: 1200px) {
    && {
      padding: 30px 60px;
    }
  }

  @media (max-width: 475px) {
    && {
      padding: 40px 20px;
    }
  }
`;

export const ProductGrid = styled(FlexGrid)`
  && {
    justify-content: flex-start;
  }
`;

export const Title = styled(Typography).attrs({ as: "h1" })`
  && {
    margin: 0;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: clamp(1.875rem, 3vw + 1rem, 3.125rem);
    line-height: 1.36;

    color: ${colors.primary};
  }
`;

export const Subtitle = styled(Typography).attrs({ as: "h2" })`
  && {
    margin: 0 0 10px;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 26px;

    color: ${colors.secondary};
  }

  @media (max-width: 475px) {
    && {
      font-size: 16px;
      line-height: 21px;
    }
  }
`;

export const Text = styled(Typography).attrs({ as: "p" })`
  && {
    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 34px;

    color: ${colors.lightGrayText};
  }
`;

export const ImageText = styled(Typography).attrs({ as: "p" })`
  && {
    margin: 0 auto;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    text-align: center;

    color: ${colors.secondary};
  }
`;

export const IconsImage = styled.img`
  && {
    display: block;
    width: 60px;
    height: 60px;
    margin: 0 auto 10px;
    object-fit: contain;
  }
`;

export const IconsList = styled.img`
  && {
    display: block;
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }
`;

export const IconsGrid = styled(Grid)`
  && {
    padding: 5px;
    text-align: center;
  }
`;

export const BottomText = styled(Typography).attrs({ as: "p" })`
  && {
    position: relative;
    top: -20px;

    margin: 0;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    color: ${colors.grayText};
  }
`;

export const List = styled(Box)`
  && {
    display: flex;
    align-items: baseline;
    margin-bottom: 15px;
  }

  && img {
    flex-shrink: 0;
  }
`;

export const PlanCard = styled(CardMUI)`
  && {
    margin-bottom: 15px;

    background: #ffffff;
    border-radius: 4px;
    box-shadow: 0px 0px 8px 1px rgba(0, 0, 0, 0.25);
  }
`;

export const CardHeader = styled(Box)<CardHeaderProps>`
  && {
    min-height: 40px;
    padding: 10px 20px;

    display: flex;
    align-items: center;

    background-color: ${({ $backgroundColor }) =>
      $backgroundColor ?? colors.secondary};

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;

    color: #ffffff;
  }
`;

export const BenefitBox = styled(Box)`
  && {
    padding: 20px 10px;
  }
`;

export const BenefitDivider = styled(Divider)`
  && {
    height: 1px;
    margin: 0 20px;

    background-color: ${colors.divider};
  }
`;

export const BenefitImage = styled.img`
  && {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

export const BenefitTitle = styled(Typography).attrs({ as: "h3" })`
  && {
    margin: 0 0 10px;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;

    color: ${colors.primary};
  }
`;

export const BenefitText = styled(Typography).attrs({ as: "p" })`
  && {
    margin: 0;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;

    white-space: pre-wrap;

    color: ${colors.grayText};
  }
`;

export const BenefitObs = styled(Typography).attrs({ as: "p" })`
  && {
    margin: 0;
    padding-bottom: 40px;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    color: #000000;
  }
`;

export const MenuButton = styled(Button)`
  && {
    display: flex;
    align-items: center;

    min-width: auto;
    height: 30px;
    margin-left: 10px;
    padding: 0;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 30px;

    text-transform: none;
    text-decoration-line: underline;

    color: ${colors.orange};
  }

  &&:hover {
    background-color: transparent;
    color: ${colors.orangeHover};
    text-decoration-line: underline;
  }

  &&:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: 475px) {
    && {
      font-size: 12px;
    }
  }
`;

export const StyledMenu = styled(Menu)`
  && .MuiPaper-root {
    padding: 15px 0;
    border-radius: 8px;
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  && {
    justify-content: center;
    padding: 0 60px;
  }

  &&:hover {
    background-color: rgba(255, 80, 0, 0.08);
  }
`;

export const Item = styled(Button)`
  && {
    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 26px;

    text-align: center;
    text-transform: none;

    color: ${colors.orange};
  }

  &&:hover {
    background-color: transparent;
    color: ${colors.orangeHover};
  }
`;

export const MenuDivider = styled(Divider)`
  && {
    width: 85%;
    height: 1px;
    margin: 18px auto;

    background-color: #dadada;
  }
`;

export const RegistryBox = styled(Box)`
  && {
    margin: 40px 0;
  }
`;

export const RegistryText = styled(Typography).attrs({ as: "p" })`
  && {
    margin: 0 0 4px;

    font-family: Figtree, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;

    color: ${colors.registry};
  }
`;